/**
 * Проверка карточки ВНН перед отправкой на согласование — один в один как в
 * мобильном приложении и на сервере (backend/catalogs/report_required_fields.py):
 * те же правила, тот же порядок, те же тексты. Панель «Замечания» показывает
 * ровно то, из-за чего сервер не даст отправить или одобрить карточку.
 *
 * Проверяется карточка в том виде, в каком она уходит на сервер (transformFormsData).
 *
 * Как в МП:
 * - поле-ссылка заполнено, только если запись есть в справочнике;
 * - «покрыто лесом» — категория земель с id 14: от неё зависят главная порода,
 *   бонитет и обязательность ярусов; уже введённые ярусы проверяются на любой земле;
 * - урочище — по дочерним записям выбранного участкового лесничества, кадастровый номер — по флагу
 *   useSixNumberKadnomer (6 или 7 цифр в третьей группе);
 * - годы проверяются, когда введено не меньше 4 символов;
 * - очаги формы №6 — только выбранные из рассчитанных по-мобильному (mpOchags),
 *   главный — первый из них.
 */
import { isTractRequired } from './forestAddress';
import { refToId } from './handbookRef';
import { doubleOrNil, form4OchagCodes, form5OchagCodes, intOrNil } from './mpOchags';

const FORM_4 = 'FORM_4';
const FORM_5 = 'FORM_5';
const COVERED_LAND_ID = 14;
const PROTECTIVE_FORESTS_ID = 1;
// Цели ВНН (id записей справочника 19), при которых форма недоступна, —
// FormVariantRules.isAllowedForPurpose в МП (Injent/glpm 2c81d15).
const GOALS_NOT_ALLOWED = { [FORM_4]: [2, 3], [FORM_5]: [1, 5, 6] };

const CATEGORY_KEYS = ['catII', 'catIII', 'catIV', 'catVa', 'catVb', 'catVc', 'catVd', 'catVe', 'catVf'];
const DAMAGED_CATEGORY_KEYS = CATEGORY_KEYS.slice(1);
const WIND_CAUSE_CATEGORY = {
  881: ['catVb', 'V(Б)'],
  882: ['catVc', 'V(В)'],
  821: ['catVe', 'V(Д)'],
  822: ['catVf', 'V(Е)'],
};
const OCHAG_CAUSE_TYPES = ['DISEASES', 'CONDITIONAL_ROTTENING', 'STEM_PESTS', 'OTHER'];

const TIER_NUMBER_FIELDS = [
  ['visota', 'высота яруса', 3, 0],
  ['zapassuh', 'запас сухостоя', 6, 2],
  ['zapasredf', 'запас редин', 6, 2],
  ['zapasedf', 'запас единичных деревьев', 6, 2],
  ['zapaszahl', 'запас захламлённости общий', 6, 2],
  ['zapaszahllikf', 'запас захламлённости ликвидный', 6, 2],
];
const SPECIES_NUMBER_FIELDS = [
  ['vozr', 'средний возраст', 3, 0],
  ['hsr', 'средняя высота', 2, 1],
  ['dsr', 'средний диаметр', 3, 1],
];
const FORM3_NUMBER_FIELDS = [
  ['vozrpod', 'Средний возраст подроста', 2, 0],
  ['hpod', 'Средняя высота подроста', 2, 1],
  ['gustpod', 'Густота подроста, тыс. шт./га', 3, 2],
  ['hpdl', 'Средняя высота подлеска', 2, 1],
  ['reljefuklon', 'Уклон', 2, 0],
];

const KADNOMER7_RE = /^\d{2}:\d{2}:\d{7}:\d{4}$/;
const KADNOMER6_RE = /^\d{2}:\d{2}:\d{6}:\d{4}$/;
const MIN_YEAR = 1500;

// Справочники, к которым правила обращаются, если в ссылке нет своего handbookId
const CAUSES_HANDBOOK_ID = 10;
const SPECIES_HANDBOOK_ID = 31;

// --- Числа --------------------------------------------------------------------

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());

/** Число из строки с точкой или запятой; null — пусто или не число. */
const toNumber = (value) => {
  const raw = text(value).replace(/,/g, '.');
  if (!raw) return null;
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
};

/** 1.5 → «1,5»: числа в сообщениях — по-русски, через запятую. */
const ru = (number) => String(number).replace('.', ',');

const digits = (count) => (count === 1 ? '1 цифры' : `${count} цифр`);

/** Формат числа как numberFormat в МП. Пустое значение формат не нарушает. */
export const numberFormatOk = (value, integerDigits, fractionDigits = 0) => {
  const raw = text(value).replace(/,/g, '.');
  if (!raw) return true;
  const dot = raw.indexOf('.');
  let integer = dot === -1 ? raw : raw.slice(0, dot);
  const fraction = dot === -1 ? '' : raw.slice(dot + 1);
  if (integer.startsWith('-')) integer = integer.slice(1);
  if (!(integer.length >= 1 && integer.length <= integerDigits && /^\d+$/.test(integer))) return false;
  if (dot === -1) return true;
  return fraction.length <= fractionDigits && /^\d*$/.test(fraction);
};

const formatError = (label, value, integerDigits, fractionDigits = 0) => {
  if (numberFormatOk(value, integerDigits, fractionDigits)) return null;
  if (fractionDigits === 0) return `${label}: целое число, не больше ${digits(integerDigits)}`;
  return `${label}: не больше ${digits(integerDigits)} до запятой и ${fractionDigits} после`;
};

/** Год, как в МП: проверяется, когда введено не меньше 4 символов; от 1500 до текущего. */
const yearError = (label, value) => {
  const raw = value === null || value === undefined ? '' : String(value);
  if (raw.length < 4) return null;
  const current = new Date().getFullYear();
  if (/^[+-]?\d+$/.test(raw) && Number(raw) >= MIN_YEAR && Number(raw) <= current) return null;
  return `${label}: введите год от ${MIN_YEAR} до ${current}`;
};

/** Широта/долгота как geoRange + numberFormat(3, 6) в МП — только через точку. */
const coordinateErrors = (label, value, limit) => {
  const raw = text(value);
  if (!raw) return [`${label} не заполнено`];
  const unsigned = raw.startsWith('-') ? raw.slice(1) : raw;
  const outOfRange = `${label}: введите число от ${ru(-limit)} до ${ru(limit)}`;
  if (!/^\d+(\.\d+)?$/.test(unsigned)) return [outOfRange];
  const number = Number(raw);
  if (number < -limit || number > limit) return [outOfRange];
  const error = formatError(label, raw, 3, 6);
  return error ? [error] : [];
};

const areaErrors = (label, value) => {
  if (!text(value)) return [];
  const number = toNumber(value);
  if (number === null) return [`${label}: введите число`];
  if (number <= 0) return [`${label} должно быть больше 0`];
  const error = formatError(label, value, 4, 4);
  return error ? [error] : [];
};

// --- Справочники --------------------------------------------------------------

const refFilled = (value) => {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'object') return value.id !== undefined && value.id !== null && value.id !== '';
  return true;
};

const refInt = (value) => {
  const id = parseInt(refToId(value), 10);
  return Number.isNaN(id) ? null : id;
};

const findHandbook = (handbooks, id) =>
  (Array.isArray(handbooks) ? handbooks.find(h => h && String(h.id) === String(id)) : null);

/** Запись справочника по ссылке { handbookId, id }, старому полному объекту или id. */
const refRecord = (value, handbooks, fallbackHandbookId) => {
  if (!refFilled(value)) return null;
  const id = typeof value === 'object' ? value.id : value;
  const handbookIds = [typeof value === 'object' ? value.handbookId : null, fallbackHandbookId];
  for (const handbookId of handbookIds) {
    const record = findHandbook(handbooks, handbookId)?.references?.find(r => String(r.id) === String(id));
    if (record) return record;
  }
  return typeof value === 'object' && value.name !== undefined ? value : null;
};

const makeResolve = (handbooks) => (value, attr, fallbackHandbookId) => {
  const embedded = value && typeof value === 'object' ? value[attr] : undefined;
  if (embedded !== undefined && embedded !== null && embedded !== '') return embedded;
  return refRecord(value, handbooks, fallbackHandbookId)?.[attr];
};

// --- Формы --------------------------------------------------------------------

const yarusNumber = (tier, index) => text(tier?.yarushm) || String(index + 1);
const compositionsOf = (tier) => (Array.isArray(tier?.sostav) ? tier.sostav.filter(i => i && typeof i === 'object') : []);
const tiersOf = (form2) => (Array.isArray(form2.plantationByTiers) ? form2.plantationByTiers : [])
  .filter(t => t && typeof t === 'object');

const makeContext = (handbooks) => {
  const resolve = makeResolve(handbooks);
  // В МП ссылка на удалённую запись справочника читается как пустая
  const refOk = (value, fallbackHandbookId) => refFilled(value) && !!refRecord(value, handbooks, fallbackHandbookId);
  const speciesName = (item, index) => resolve(item?.kodporoda, 'name', SPECIES_HANDBOOK_ID) || `№${index}`;
  const causeNameByCode = (code) => {
    const cause = findHandbook(handbooks, CAUSES_HANDBOOK_ID)?.references?.find(r => String(r.code) === String(code));
    return cause?.name || `код ${code}`;
  };
  return { resolve, refOk, speciesName, causeNameByCode, handbooks };
};

const form1Errors = (form1, ctx, optionalTab) => {
  const errors = [
    ...coordinateErrors('Широта', form1.latitude, 90),
    ...coordinateErrors('Долгота', form1.longitude, 180),
  ];
  const vnum = formatError('ВНУМ', form1.vnum, 4);
  if (vnum) errors.push(vnum);

  [
    ['srf', 'Субъект РФ'],
    ['filial', 'Организация, проводившая работу'],
    ['lvo', 'Лесничество'],
    ['ulvo', 'Участковое лесничество'],
    ['cel', 'Цель ВНН'],
  ].forEach(([field, label]) => {
    if (!ctx.refOk(form1[field])) errors.push(`${label} не заполнено`);
  });

  // МП (Form1Schema.cel): форма №4 недоступна при целях 2 и 3, форма №5 — при 1, 5 и 6.
  if (ctx.refOk(form1.cel) && (GOALS_NOT_ALLOWED[optionalTab] || []).includes(refInt(form1.cel))) {
    errors.push(`Цель ВНН не может быть выбрана при форме №${optionalTab === FORM_4 ? 4 : 5}`);
  }

  // Как Form1Layout МП: наличие урочищ определяется по parent_id выбранного участкового.
  if (isTractRequired(form1.ulvo, ctx.handbooks, form1.isUrcRequired === true) && !ctx.refOk(form1.urc)) errors.push('Урочище не заполнено');

  [
    ['kv', 'Квартал'],
    ['vid', '№ выдела'],
    ['s', 'S, га'],
    ['lpvid', '№ лесопатологического выдела'],
    ['slp', 'S лесопатологического выдела, га'],
    ['data', 'Дата создания карточки'],
    ['dataizm', 'Дата изменения карточки'],
    ['datar', 'Дата проведения ВНН'],
  ].forEach(([field, label]) => {
    if (!text(form1[field])) errors.push(`${label} не заполнено`);
  });

  errors.push(...areaErrors('S, га', form1.s));
  errors.push(...areaErrors('S лесопатологического выдела, га', form1.slp));
  // МП: S лесопатологического выдела — от 0 до S выдела (пустое S считается нулём)
  const slp = toNumber(form1.slp) || 0;
  if (slp < 0 || slp > doubleOrNil(form1.s)) errors.push('S лесопатологического выдела больше S выдела');

  const kadnomer = form1.kadnomer === null || form1.kadnomer === undefined ? '' : String(form1.kadnomer);
  if (kadnomer) {
    const six = form1.useSixNumberKadnomer === true;
    if (!(six ? KADNOMER6_RE : KADNOMER7_RE).test(kadnomer)) {
      errors.push(six ? 'Кадастровый номер: формат 00:00:000000:0000' : 'Кадастровый номер: формат 00:00:0000000:0000');
    }
  }

  const lmh = formatError('Протяжённость маршрутного хода', form1.lmh, 6, 3);
  if (lmh) errors.push(lmh);
  return errors;
};

const tierErrors = (tier, index, ctx) => {
  const prefix = `Ярус ${yarusNumber(tier, index)}`;
  const errors = [];

  const compositions = compositionsOf(tier);
  if (compositions.length === 0) {
    errors.push(`${prefix} — не указан состав`);
  } else {
    compositions.forEach((item, i) => {
      const share = text(item.dolap);
      if (share !== '+' && !(intOrNil(share) >= 1 && intOrNil(share) <= 10)) {
        errors.push(`${prefix} — доля породы «${ctx.speciesName(item, i + 1)}» должна быть от 1 до 10 или «+»`);
      }
    });
    if (compositions.reduce((sum, item) => sum + intOrNil(item.dolap), 0) < 10) {
      errors.push(`${prefix} — сумма долей состава меньше 10`);
    }
  }

  if (!text(tier.polnota)) {
    errors.push(`${prefix} — не заполнена полнота`);
  } else {
    const format = formatError(`${prefix} — полнота`, tier.polnota, 1, 2);
    if (format) errors.push(format);
    const low = intOrNil(tier.yarushm) === 1 ? 0 : 0.3;
    const value = toNumber(tier.polnota) || 0;
    if (value < low || value > 1.5) errors.push(`${prefix} — полнота должна быть от ${ru(low)} до 1,5`);
  }

  if (!text(tier.zapassirga)) {
    errors.push(`${prefix} — не заполнено поле "Запас сырораст., кбм на га"`);
  } else {
    const value = toNumber(tier.zapassirga);
    if (value !== null && value < 0.1) errors.push(`${prefix} — запас сырорастущего должен быть не меньше 0,1 кбм на га`);
    const format = formatError(`${prefix} — запас сырорастущего`, tier.zapassirga, 6, 2);
    if (format) errors.push(format);
  }

  TIER_NUMBER_FIELDS.forEach(([field, label, integerDigits, fractionDigits]) => {
    const error = formatError(`${prefix} — ${label}`, tier[field], integerDigits, fractionDigits);
    if (error) errors.push(error);
  });
  return errors;
};

const eachComposition = (tiers, ctx, callback) => {
  tiers.forEach((tier, index) => {
    compositionsOf(tier).forEach((item, i) => {
      callback(`Ярус ${yarusNumber(tier, index)}, порода «${ctx.speciesName(item, i + 1)}»`, item);
    });
  });
};

const form2Errors = (form2, ctx) => {
  const general = form2.generalPlantationInfo || {};
  const tiers = tiersOf(form2);
  const errors = [];

  if (form2.landAvailability === null || form2.landAvailability === undefined) {
    errors.push('Доступность участка не заполнено');
  }
  if (form2.useActualData === null || form2.useActualData === undefined) {
    errors.push('Не выбрано, по каким данным заполнена форма: материалы ТО или фактические');
  }

  if (!ctx.refOk(form2.cnl)) errors.push('Целевое назначение лесов не заполнено');
  if (refInt(form2.cnl) === PROTECTIVE_FORESTS_ID && !ctx.refOk(form2.kzl)) {
    errors.push('Категория защитных лесов не заполнено');
  }

  if (!text(form2.glu)) {
    errors.push('Год лесоустройства не заполнено');
  } else {
    const glu = yearError('Год лесоустройства', form2.glu);
    if (glu) errors.push(glu);
  }

  if (!ctx.refOk(general.katzem)) errors.push('Категория земель не заполнено');
  // Главная порода, бонитет и ярусы обязательны на землях, покрытых лесом
  const covered = refInt(general.katzem) === COVERED_LAND_ID;
  if (covered && !ctx.refOk(general.gporoda)) errors.push('Главная порода не заполнено');
  if (covered && !ctx.refOk(general.bonitet)) errors.push('Бонитет не заполнено');
  if (covered && tiers.length === 0) errors.push('Не заполнены ярусы');

  // Введённые ярусы проверяются на любой земле — как в МП
  tiers.forEach((tier, index) => errors.push(...tierErrors(tier, index, ctx)));
  eachComposition(tiers, ctx, (label, item) => {
    SPECIES_NUMBER_FIELDS.forEach(([field, name, integerDigits, fractionDigits]) => {
      const error = formatError(`${label} — ${name}`, item[field], integerDigits, fractionDigits);
      if (error) errors.push(error);
    });
  });

  // Первая порода 1-го яруса — это главная порода (MainSpeciesMismatch в МП)
  const firstTier = tiers.find(t => intOrNil(t.yarushm) === 1);
  const firstSpecies = compositionsOf(firstTier)[0];
  if (firstSpecies && refInt(firstSpecies.kodporoda) !== refInt(general.gporoda)) {
    errors.push('Главная порода не совпадает с первой породой 1-го яруса');
  }
  return errors;
};

const form3Errors = (form3) => FORM3_NUMBER_FIELDS
  .map(([field, label, integerDigits, fractionDigits]) => formatError(label, form3[field], integerDigits, fractionDigits))
  .filter(Boolean);

// Ограничения степперов Table4/5DetailsLayout в МП (master 0621acc8c1a0d92351308da40e22ca53109285c3).
const categoryErrors = (prefix, variant, keys, label) => {
  const values = keys.map(key => text(variant[key]));
  const errors = [];
  if (values.some(value => value && (!/^\d+$/.test(value) || Number(value) > 100))) {
    errors.push(`${prefix} — ${label}: целые числа от 0 до 100`);
  }
  if (values.reduce((sum, value) => sum + (/^\d+$/.test(value) ? Number(value) : 0), 0) > 100) {
    errors.push(`${prefix} — сумма долей (${label}) больше 100 %`);
  }
  return errors;
};

const damageSignErrors = (prefix, number, sign, categories, damagedTotal, ctx) => {
  const prichina = sign.prichina;
  if (!ctx.refOk(prichina, CAUSES_HANDBOOK_ID)) return [`${prefix} — причина повреждения №${number} не выбрана`];
  const cause = ctx.resolve(prichina, 'name', CAUSES_HANDBOOK_ID) || `№${number}`;
  const signPrefix = `${prefix}, причина «${cause}»`;
  const errors = [];

  const signs = Array.isArray(sign.priznList) ? sign.priznList : [];
  if (signs.length === 0 || !signs.every(item => ctx.refOk(item))) {
    errors.push(`${signPrefix} — не указан признак повреждения`);
  }

  const year = yearError(`${signPrefix} — год повреждения`, sign.godpovr);
  if (year) errors.push(year);

  const damaged = intOrNil(sign.dolaprizn);
  if (!text(sign.dolaprizn)) {
    errors.push(`${signPrefix} — не заполнена доля повреждённых деревьев`);
  } else {
    if (damaged < 0 || damaged > damagedTotal) {
      errors.push(`${signPrefix} — доля повреждённых деревьев должна быть от 0 до ${damagedTotal}`);
    }
    const code = parseInt(ctx.resolve(prichina, 'code', CAUSES_HANDBOOK_ID), 10);
    const wind = WIND_CAUSE_CATEGORY[code];
    if (wind && damaged !== categories[wind[0]]) {
      errors.push(`${signPrefix} — доля повреждённых деревьев должна совпадать с категорией ${wind[1]}`);
    }
  }

  const specific = sign.specificDolaPrizn;
  if (!text(specific) && OCHAG_CAUSE_TYPES.includes(ctx.resolve(prichina, 'type', CAUSES_HANDBOOK_ID))) {
    errors.push(`${signPrefix} — не заполнена доля повреждённых деревьев для определения очага`);
  } else if (intOrNil(specific) < 0 || intOrNil(specific) > damaged) {
    errors.push(`${signPrefix} — доля для определения очага должна быть от 0 до ${damaged}`);
  }
  return errors;
};

const form4Errors = (tiers, ctx) => {
  const errors = [];
  eachComposition(tiers, ctx, (prefix, item) => {
    const variant4 = item.variant4 || {};
    errors.push(...categoryErrors(prefix, variant4, CATEGORY_KEYS, 'категории состояния'));
    const categories = Object.fromEntries(CATEGORY_KEYS.map(key => [key, intOrNil(variant4[key])]));
    const damagedTotal = Object.values(categories).reduce((sum, value) => sum + value, 0);
    const signs = (Array.isArray(variant4.damageSigns) ? variant4.damageSigns : []).filter(s => s && typeof s === 'object');
    // Как в МП: есть деревья III–V категорий и I категория меньше 100 %
    const damaged = DAMAGED_CATEGORY_KEYS.some(key => categories[key] !== 0) && damagedTotal > 0;
    if (damaged && signs.length === 0 && text(item.dolap) !== '+') {
      errors.push(`${prefix} — не указана причина повреждения`);
    }
    signs.forEach((sign, i) => errors.push(...damageSignErrors(prefix, i + 1, sign, categories, damagedTotal, ctx)));
  });
  return errors;
};

const form5Errors = (tiers, ctx) => {
  const errors = [];
  eachComposition(tiers, ctx, (prefix, item) => {
    const causes = Array.isArray(item.variant5?.prichini) ? item.variant5.prichini : [];
    errors.push(...categoryErrors(prefix, item.variant5 || {}, ['cat25', 'cat50', 'cat75', 'cat100'], 'повреждение крон'));
    // В Variant5DistributionSchema пустой список разрешён; проверяются его элементы.
    if (!causes.every(cause => ctx.refOk(cause, CAUSES_HANDBOOK_ID))) {
      errors.push(`${prefix} — есть пустая причина повреждения`);
    }
  });
  return errors;
};

const form6Errors = (form6, form1, ochagCodes, ctx) => {
  const errors = [];
  const required = text(form6.activities_required).toUpperCase();
  if (required !== 'YES' && required !== 'NO') errors.push('Не указана необходимость назначения мероприятий');
  if (required === 'YES') {
    const rows = (Array.isArray(form6.recommendedActivities) ? form6.recommendedActivities : [])
      .filter(row => row && typeof row === 'object');
    if (rows.length === 0) errors.push('Не добавлено ни одного рекомендуемого мероприятия');
    // Площадь мероприятия — от 0,1 до S лесопатологического выдела (или S выдела)
    const maximum = doubleOrNil(text(form1.slp) ? form1.slp : form1.s);
    rows.forEach((row, i) => {
      const number = i + 1;
      if (!ctx.refOk(row.rekvidmer)) errors.push(`Мероприятие ${number} — не выбран вид мероприятия`);
      const format = formatError(`Мероприятие ${number} — площадь`, row.srekmer, 4, 4);
      if (format) errors.push(format);
      const value = toNumber(row.srekmer);
      if (value !== null && (value < 0.1 || value > maximum)) {
        errors.push(`Мероприятие ${number} — площадь должна быть от 0,1 до ${ru(maximum)} га`);
      }
    });
  }

  // Выбранные очаги — только рассчитанные (validCodes в МП); у каждого — вид ВО,
  // у главного (первого) — фаза и тип. Запись без данных — тоже ошибка.
  const vidvo = form6.vidvo && typeof form6.vidvo === 'object' ? form6.vidvo : {};
  const selected = [];
  (Array.isArray(form6.selectedOchagCodes) ? form6.selectedOchagCodes : []).forEach((raw) => {
    const code = parseInt(String(raw).split('-')[0], 10);
    if (!Number.isNaN(code) && ochagCodes.includes(code) && !selected.includes(code)) selected.push(code);
  });
  selected.forEach((code, position) => {
    const entry = vidvo[String(code)] && typeof vidvo[String(code)] === 'object' ? vidvo[String(code)] : {};
    if (!ctx.refOk(entry.vidvo)) {
      errors.push(`Очаг «${ctx.causeNameByCode(code)}» — не указан вид вредного организма`);
    }
    if (position === 0) {
      if (!text(entry.ochagPhasa)) errors.push(`Главный очаг «${ctx.causeNameByCode(code)}» — не указана фаза`);
      if (!text(entry.ochagType)) errors.push(`Главный очаг «${ctx.causeNameByCode(code)}» — не указан тип`);
    }
  });
  return errors;
};

/**
 * Ошибки карточки по правилам мобильного приложения.
 * @param {object} card — карточка в формате сервера (transformFormsData)
 * @param {Array} handbooks
 * @returns {Array<{form: string, message: string}>} form — 'Form1' … 'Form6' для панели «Замечания»
 */
export const validateCardRules = (card, handbooks = []) => {
  const ctx = makeContext(handbooks);
  const form1 = card?.form1 || {};
  const form2 = card?.form2 || {};
  const tiers = tiersOf(form2);
  const optionalTab = text(card?.optionalTab).toUpperCase();
  const tagged = (form, messages) => messages.map(message => ({ form, message }));

  const result = [
    ...tagged('Form1', form1Errors(form1, ctx, optionalTab)),
    ...tagged('Form2', form2Errors(form2, ctx)),
    ...tagged('Form3', form3Errors(card?.form3 || {})),
  ];
  let ochagCodes = [];
  if (optionalTab === FORM_4) {
    result.push(...tagged('Form4', form4Errors(tiers, ctx)));
    ochagCodes = form4OchagCodes(form1, form2, ctx.resolve);
  } else if (optionalTab === FORM_5) {
    result.push(...tagged('Form5', form5Errors(tiers, ctx)));
    ochagCodes = form5OchagCodes(form1, form2, ctx.resolve);
  } else {
    result.push({ form: 'Form4', message: 'Не выбрана форма №4 или №5' });
  }
  // Очаги выбираются на вкладке формы №4/№5 — ошибки по ним показываем там же
  const ochagForm = optionalTab === FORM_5 ? 'Form5' : 'Form4';
  form6Errors(card?.form6 || {}, form1, ochagCodes, ctx).forEach((message) => {
    result.push({ form: /очаг/i.test(message) ? ochagForm : 'Form6', message });
  });
  return result;
};
